"use client";

import { css } from "@emotion/react";
import { Popover, Sidetab } from "@typeform/embed-react";
import type { PopoverProps, SidetabProps } from "@typeform/embed-react";
import { useAtomValue } from "jotai";
import { useRouter } from "next/router";
import { type FunctionComponent, memo, useCallback, useEffect, useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { safeSetLocalStorage } from "scmp-sdk";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { useResponsive } from "shared/hooks";
import { accountAtom } from "shared/lib/account";
import { theme } from "shared/lib/styles";
import { parseQueryString } from "shared/lib/utils";

import { isShowTakeoverAdSlotAtom } from "scmp-app/components/advertisement/takeover-ad-slot/atoms";

import { TypeformConfigMap } from "./consts";
import type { TypeformConfig, TypeformConfigKey } from "./types";

type Props = {
  configKey?: TypeformConfigKey;
  testing_group?: string;
  utm_content?: string;
  utm_source?: null | string;
  utm_term?: string;
};

export const TypeformComponent: FunctionComponent<Props> = (props: Props) => {
  const { configKey, testing_group, utm_content, utm_source, utm_term } = props;
  const config = configKey ? (TypeformConfigMap[configKey] as TypeformConfig) : undefined;

  const { user } = useAtomValue(accountAtom);
  const isShowTakeoverAdSlot = Boolean(useAtomValue(isShowTakeoverAdSlotAtom));
  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"), false);
  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);

  const { query } = useRouter();
  const searchQuery = useMemo(() => parseQueryString(query.q) ?? "", [query.q]);

  const [isShowForm, setIsShowForm] = useState(false);
  const [shouldAutoOpen, setShouldAutoOpen] = useState(false);

  useEffect(() => {
    if (!config?.key || localStorage?.getItem(config?.key) === "1") return;

    setIsShowForm(true);

    const shouldAutoOpenForDevice = isMobile
      ? config.shouldAutoOpen.mobile
      : config.shouldAutoOpen.desktop;

    if (shouldAutoOpenForDevice) {
      const timer = setTimeout(() => {
        setShouldAutoOpen(true);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isMobile, config?.key, config?.shouldAutoOpen.desktop, config?.shouldAutoOpen.mobile]);

  const onFormFinished = useCallback(() => {
    safeSetLocalStorage(config!.key, "1");
  }, [config]);

  const onReady = useCallback(() => {
    if (config?.treatAsFinished === "onReady") {
      onFormFinished();
    }
  }, [config?.treatAsFinished, onFormFinished]);

  const onSubmit = useCallback(() => {
    if (config?.treatAsFinished === "onSubmit") {
      onFormFinished();
    }
  }, [config?.treatAsFinished, onFormFinished]);

  const onClose = () => {
    setIsShowForm(false);
  };

  const hiddenFields = useMemo(
    () => ({
      ...(testing_group && { testing_group }),
      utm_content: utm_content ?? searchQuery,
      ...(utm_source !== null && { utm_source: utm_source ?? (isMobile ? "mdot" : "desk") }),
      utm_term: utm_term ?? user?.uuid ?? "",
    }),
    [testing_group, utm_content, utm_source, utm_term, isMobile, searchQuery, user?.uuid],
  );

  const formProps = useMemo<PopoverProps | SidetabProps>(
    () => ({
      height: 600,
      hidden: hiddenFields,
      hideHeaders: true,
      id: config?.id ?? "", // null check will be handled in the render function
      onClose,
      onReady,
      onSubmit,
      open: shouldAutoOpen ? "load" : undefined,
    }),
    [config?.id, hiddenFields, onReady, onSubmit, shouldAutoOpen],
  );

  const render = useCallback(() => {
    if (!isShowForm || !config?.id) return null;

    if (config.variant?.sidetab !== undefined) {
      const { buttonColor = "#ffcb05", buttonText, hideIcon } = config.variant.sidetab;

      const customSidetabCss = css`
        ${".tf-v1-sidetab-button-icon"} {
          ${theme.breakpoints.only("mobile")} {
            display: ${hideIcon?.mobile ? "none" : "block"};
          }
          ${theme.breakpoints.only("tablet")} {
            display: ${hideIcon?.tablet ? "none" : "block"};
          }
          ${theme.breakpoints.up("desktop")} {
            display: ${hideIcon?.desktop ? "none" : "block"};
          }
        }
      `;

      return (
        <>
          <style>{customSidetabCss.styles}</style>
          <Sidetab
            {...formProps}
            buttonColor={buttonColor}
            buttonText={buttonText}
            medium="sidetab"
          />
        </>
      );
    }

    const { buttonColor = "#ffcb05" } = config.variant?.popover ?? {};
    const takeoverOffset = isDesktopUp ? "96px" : "56px";

    const customPopoverCss = css`
      ${".tf-v1-popover-button"} {
        ${isShowTakeoverAdSlot ? `bottom: ${takeoverOffset} !important;` : ""}
        svg {
          path {
            fill: #001246;
          }
        }
      }
    `;

    return (
      <>
        <style>{customPopoverCss.styles}</style>
        <Popover {...formProps} buttonColor={buttonColor} medium="popover" />
      </>
    );
  }, [config, formProps, isDesktopUp, isShowForm, isShowTakeoverAdSlot]);

  return (
    <ErrorBoundary fallback={<></>}>
      <ClientSideSuspense>{render()}</ClientSideSuspense>
    </ErrorBoundary>
  );
};

export const Typeform = memo(TypeformComponent);
